import { BrandMarginConfig, MarginConfigSummary, MarginOverviewRow, MarginState, MarginTemplate } from '../../../types/MarginValuation'
import { mergeActive, mergeAmounts } from './mergeConflict'
import { buildSiteLabelGroups, groupBy } from './overviewUtils'

export function toDisplay(stored: number): string {
    return (stored / 100).toString()
}

export function toStored(display: string): number {
    return Math.round(parseFloat(display) * 100)
}

// Limite la saisie à 2 décimales pendant la frappe (pas un arrondi final, juste un garde-fou
// visuel — le stockage arrondit de toute façon via toStored).
export function sanitizeMargin(value: string): string {
    const parts = value.split('.')
    if (parts.length === 2 && parts[1].length > 2) {
        return `${parts[0]}.${parts[1].slice(0, 2)}`
    }
    return value
}

// Convertit la ligne brute de l'API en état d'édition (garde une copie "original" pour détecter
// les modifications non sauvegardées) ; état vide/inactif si rien n'est encore configuré.
export function buildGlobalConfig(data: MarginTemplate | null): MarginState {
    if (!data) {
        return { margin: '', isActive: false, originalMargin: '', originalIsActive: false }
    }
    const margin = toDisplay(data.margin)
    return { id: data.id, margin, isActive: data.is_active, originalMargin: margin, originalIsActive: data.is_active }
}

export function formatMargin(margin: number | null): string {
    return margin !== null ? `${margin} %` : '-'
}

/**
 * Valeur par défaut quand plusieurs sites ou plusieurs marques sont sélectionnés et ont des
 * valeurs divergentes déjà en base : marge la plus élevée, actif dès qu'au moins une entité
 * l'est.
 */
export function mergeMargin(
    fallback: MarginState | null,
    entityRows: Array<MarginOverviewRow | undefined>,
): { config: MarginState, marginConflict: boolean, activeConflict: boolean } {
    const fallbackMargin = fallback ? parseFloat(fallback.margin) : null
    const fallbackActive = fallback?.isActive ?? false

    const effective = entityRows.map(row => ({
        margin: row ? row.margin / 100 : fallbackMargin,
        isActive: row ? row.is_active : fallbackActive,
    }))

    const { value: margin, hasConflict: marginConflict } = mergeAmounts(effective.map(e => e.margin))
    const { value: isActive, hasConflict: activeConflict } = mergeActive(effective.map(e => e.isActive))

    const marginDisplay = margin !== null ? String(margin) : ''

    return {
        config: { margin: marginDisplay, isActive, originalMargin: marginDisplay, originalIsActive: isActive },
        marginConflict,
        activeConflict,
    }
}

// Résumé affichable pour l'aperçu : margin=null si aucune ligne (pas encore configuré).
export function toSummary(row: MarginOverviewRow | undefined): MarginConfigSummary {
    return row ? { margin: row.margin / 100, isActive: row.is_active } : { margin: null, isActive: false }
}

function signatureOf(summary: MarginConfigSummary): string {
    return `${summary.margin ?? 'null'}:${summary.isActive}`
}

// Une seule ligne par site (pas un tableau : la marge est une valeur unique, pas une liste de
// frais) — exclut les lignes génériques marque-seule/globales.
export function groupByDealershipId(rows: MarginOverviewRow[]): Map<number, MarginOverviewRow> {
    const grouped = new Map<number, MarginOverviewRow>()
    for (const row of rows) {
        if (row.dealership_id !== null) {
            grouped.set(row.dealership_id, row)
        }
    }
    return grouped
}

// Pour l'aperçu : une ligne par marque, avec ses sites regroupés quand ils ont exactement la
// même config (même signature), + une ligne générique "-" si la marque a une marge sans site.
export function buildBrandConfigs(
    brandScopedData: MarginOverviewRow[],
    siteNameById: Map<number, string>,
): BrandMarginConfig[] {
    const byBrand = groupBy(brandScopedData, row => row.brand as string)

    return Array.from(byBrand.entries()).flatMap(([ brand, rows ]) => {
        const genericRow = rows.find(row => row.dealership_id === null)
        const siteRowById = groupByDealershipId(rows)

        const siteSummaries = Array.from(siteRowById.entries()).map(([ dealershipId, row ]) => {
            const value = toSummary(row)
            return { dealershipId, signature: signatureOf(value), value }
        })

        const brandConfigs: BrandMarginConfig[] = buildSiteLabelGroups(siteSummaries, siteNameById)
            .map(({ siteLabel, dealershipIds, value }) => ({ brand, siteLabel, dealershipIds, ...value }))

        if (genericRow) {
            brandConfigs.push({ brand, siteLabel: '-', dealershipIds: [], ...toSummary(genericRow) })
        }

        return brandConfigs
    })
}
